import React, { useState, useEffect, useMemo } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineUser,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
  HiOutlineLockClosed,
  HiOutlineShieldCheck
} from 'react-icons/hi2';
import { authService, masterDataService, plantService } from '../services';
import userService from '../services/userService';
import { toast } from 'react-toastify';
import SharedModal from '../components/common/SharedModal';
import PageLoading from '../components/common/PageLoading';

import './Users.css';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [plants, setPlants] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [activeTab, setActiveTab] = useState('profile'); // 'profile' or 'access'
  const [formData, setFormData] = useState({
    fullName: '',
    username: '',
    email: '',
    phone: '',
    roleId: '',
    departmentId: '',
    designation: '',
    status: 'Active',
    password: '',
    confirmPassword: '',
    plantIds: [],
    defaultPlantId: null, // the user's home/default plant
    enableEmail: false,
    enableWhatsApp: false,
    enableSMS: false,
    enableNotification: false,
    hasLogin: true // Toggle for system access
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [usernameCheck, setUsernameCheck] = useState({ checking: false, available: null });

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadData();
    } else {
      setError('Please login to view users');
      setLoading(false);
    }
  }, [tenantId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [usersData, rolesData, plantsData, departmentsData] = await Promise.all([
        userService.getAllUsers(),
        masterDataService.getRoles(tenantId),
        plantService.getAllPlants(),
        masterDataService.getDepartments(tenantId)
      ]);

      // Merge with session-stored notification settings (static persistence)
      const storedSettings = JSON.parse(sessionStorage.getItem('user_notifications_cache') || '{}');
      const mergedUsers = usersData.map(user => ({
        ...user,
        ...(storedSettings[user.userId] || {
          enableEmail: false,
          enableWhatsApp: false,
          enableSMS: false,
          enableNotification: false
        })
      }));

      setUsers(mergedUsers);
      setRoles(rolesData);
      setPlants(plantsData.filter(plant => plant.status === 'Active'));
      setDepartments(departmentsData);
    } catch (err) {
      const msg = 'Failed to load data. Please try again.';
      setError(msg);
      toast.error(msg);
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredUsers = users.filter((user) =>
    user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingUser(null);
    setFormData({
      fullName: '',
      username: '',
      email: '',
      phone: '',
      roleId: '',
      departmentId: '',
      designation: '',
      status: 'Active',
      password: '',
      confirmPassword: '',
      plantIds: [],
      defaultPlantId: null,
      enableEmail: false,
      enableWhatsApp: false,
      enableSMS: false,
      enableNotification: false,
      hasLogin: false // Default to no login for new employees
    });
    setFormErrors({});
    setUsernameCheck({ checking: false, available: null });
    setActiveTab('profile');
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      fullName: user.fullName || '',
      username: user.username || '',
      email: user.email || '',
      phone: user.phone || '',
      roleId: user.roleId || '',
      departmentId: user.departmentId || '',
      designation: user.designation || '',
      status: user.status || 'Active',
      password: '',
      confirmPassword: '',
      plantIds: user.plantIds || [],
      defaultPlantId: (user.plantIds?.length === 1) ? user.plantIds[0] : null,
      enableEmail: user.enableEmail || false,
      enableWhatsApp: user.enableWhatsApp || false,
      enableSMS: user.enableSMS || false,
      enableNotification: user.enableNotification || false,
      hasLogin: !!user.username // If they have a username, they have a login
    });
    setFormErrors({});
    setUsernameCheck({ checking: false, available: null });
    setActiveTab('profile');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const toastId = toast.loading('Deleting user...');
    try {
      setError('');
      await userService.deleteUser(id);
      await loadData();
      setDeleteConfirm(null);
      toast.update(toastId, { render: 'User deleted successfully!', type: 'success', isLoading: false, autoClose: 3000, closeButton: true });
    } catch (err) {
      const msg = 'Failed to delete user. Please try again.';
      setError(msg);
      toast.update(toastId, { render: msg, type: 'error', isLoading: false, autoClose: 3000, closeButton: true });
      console.error('Error deleting user:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({ ...prev, [name]: val }));
    
    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Check username availability when username changes (only for new users)
    if (name === 'username' && !editingUser && val) {
      checkUsernameAvailability(val);
    }
  };

  const checkUsernameAvailability = async (username) => {
    if (!username || username.length < 3) {
      setUsernameCheck({ checking: false, available: null });
      return;
    }

    setUsernameCheck({ checking: true, available: null });
    try {
      const result = await userService.checkUsername(username);
      setUsernameCheck({ checking: false, available: result.available });
    } catch (err) {
      setUsernameCheck({ checking: false, available: null });
    }
  };

  const validateForm = () => {
    const errors = {};

    // Full Name: required, letters and spaces only, min 3 chars
    if (!formData.fullName.trim()) {
      errors.fullName = 'Full name is required';
    } else if (formData.fullName.trim().length < 3) {
      errors.fullName = 'Full name must be at least 3 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.fullName.trim())) {
      errors.fullName = 'Full name can only contain letters and spaces';
    }

    // Email: valid format when provided
    if (formData.email && formData.email.trim()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        errors.email = 'Please enter a valid email address (e.g. user@example.com)';
      }
    }

    // Phone: digits/spaces/hyphens/+ only, 7–15 digits when provided
    if (formData.phone && formData.phone.trim()) {
      const digitsOnly = formData.phone.replace(/[\s\-+]/g, '');
      if (!/^[\d\s\-+]+$/.test(formData.phone.trim())) {
        errors.phone = 'Phone number can only contain digits, spaces, +, and -';
      } else if (digitsOnly.length < 7 || digitsOnly.length > 15) {
        errors.phone = 'Phone number must be between 7 and 15 digits';
      }
    }

    if (formData.hasLogin) {
      if (!formData.username.trim()) {
        errors.username = 'Username is required for system login';
      } else if (formData.username.length < 3) {
        errors.username = 'Username must be at least 3 characters';
      } else if (!editingUser && usernameCheck.available === false) {
        errors.username = 'Username is already taken';
      }

      if (!formData.roleId) {
        errors.roleId = 'User role is required';
      }

      if (!editingUser) {
        if (!formData.password) {
          errors.password = 'Password is required';
        } else if (formData.password.length < 6) {
          errors.password = 'Password must be at least 6 characters';
        }
        if (formData.password !== formData.confirmPassword) {
          errors.confirmPassword = 'Passwords do not match';
        }
      } else if (formData.password) {
        if (formData.password.length < 6) {
          errors.password = 'Password must be at least 6 characters';
        }
        if (formData.password !== formData.confirmPassword) {
          errors.confirmPassword = 'Passwords do not match';
        }
      }
    }

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      const profileFields = ['fullName', 'email', 'phone'];
      const hasProfileErrors = profileFields.some(f => errors[f]);
      const hasAccessErrors = Object.keys(errors).some(f => !profileFields.includes(f));

      if (activeTab === 'profile' && !hasProfileErrors && hasAccessErrors) {
        setActiveTab('access');
      } else if (activeTab === 'access' && !hasAccessErrors && hasProfileErrors) {
        setActiveTab('profile');
      }
    }

    return Object.keys(errors).length === 0;
  };

  // Restrict phone input to digits, +, -, and spaces only
  const handlePhoneKeyDown = (e) => {
    const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (allowed.includes(e.key)) return;
    if (/^[\d\s\-+]$/.test(e.key)) return;
    e.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const toastId = toast.loading(editingUser ? 'Updating user...' : 'Creating user...');
    try {
      setSubmitting(true);
      setError('');

      const userData = {
        fullName: formData.fullName.trim(),
        username: formData.hasLogin ? formData.username.trim() : `emp_${Date.now()}`, // Temporary fallback if backend requires username
        email: formData.email?.trim() || null,
        phone: formData.phone?.trim() || null,
        roleId: formData.hasLogin && formData.roleId ? parseInt(formData.roleId) : null,
        departmentId: formData.departmentId ? parseInt(formData.departmentId) : null,
        designation: formData.designation?.trim() || null,
        status: formData.status,
        plantIds: formData.hasLogin ? (formData.plantIds || []) : [],
        defaultPlantId: formData.hasLogin ? (formData.defaultPlantId ?? null) : null,
      };

      // Only include password if hasLogin and provided
      if (formData.hasLogin && formData.password) {
        userData.password = formData.password;
      } else if (!formData.hasLogin && !editingUser) {
        // If no login, we might still need a dummy password for the backend if it's mandatory
        userData.password = 'Employee@123'; 
      }

      if (editingUser) {
        userData.userId = editingUser.userId;
        await userService.updateUser(editingUser.userId, userData);
        
        const cache = JSON.parse(sessionStorage.getItem('user_notifications_cache') || '{}');
        cache[editingUser.userId] = {
          enableEmail: formData.enableEmail,
          enableWhatsApp: formData.enableWhatsApp,
          enableSMS: formData.enableSMS,
          enableNotification: formData.enableNotification
        };
        sessionStorage.setItem('user_notifications_cache', JSON.stringify(cache));
      } else {
        const result = await userService.createUser(userData);
        const newUserId = result?.userId || result?.id;
        
        if (newUserId) {
          const cache = JSON.parse(sessionStorage.getItem('user_notifications_cache') || '{}');
          cache[newUserId] = {
            enableEmail: formData.enableEmail,
            enableWhatsApp: formData.enableWhatsApp,
            enableSMS: formData.enableSMS,
            enableNotification: formData.enableNotification
          };
          sessionStorage.setItem('user_notifications_cache', JSON.stringify(cache));
        }
      }

      await loadData();
      setShowModal(false);
      handleCloseModal();
      toast.update(toastId, { render: editingUser ? 'User updated successfully!' : 'User created successfully!', type: 'success', isLoading: false, autoClose: 3000, closeButton: true });
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save. Please try again.';
      setError(errorMessage);
      toast.update(toastId, { render: errorMessage, type: 'error', isLoading: false, autoClose: 3000, closeButton: true });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      fullName: '',
      username: '',
      email: '',
      phone: '',
      roleId: '',
      departmentId: '',
      designation: '',
      status: 'Active',
      password: '',
      confirmPassword: '',
      plantIds: [],
      defaultPlantId: null,
      enableEmail: false,
      enableWhatsApp: false,
      enableSMS: false,
      enableNotification: false,
      hasLogin: false
    });
    setFormErrors({});
    setUsernameCheck({ checking: false, available: null });
    setActiveTab('profile');
  };

  const getRoleName = (roleId) => {
    const role = roles.find(r => r.roleId === roleId);
    return role ? role.roleName : '-';
  };

  const summaryStats = useMemo(() => {
    return {
      total: users.length,
      active: users.filter(u => u.status?.toLowerCase() === 'active').length,
      inactive: users.filter(u => u.status?.toLowerCase() === 'inactive').length
    };
  }, [users]);

  if (loading) {
    return (
      <div className="page-container users-page">
        <PageLoading message="Loading users data..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer users-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Users & Employees</h1>
          <p>Manage plant personnel and system access</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleAdd}>
            <HiOutlinePlus />
            Add Person
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="users-summary-cards">
        <div className="summary-card">
          <span className="summary-label kpi-label">Total Staff</span>
          <strong className="summary-value kpi-value empty">{summaryStats.total}</strong>
          <div>
            <HiOutlineUser className="kpi-icon" />
          </div>
        </div>
        <div className="summary-card">
          <span className="summary-label kpi-label">Active</span>
          <strong className="summary-value kpi-value empty">{summaryStats.active}</strong>
          <div>
            <svg className="kpi-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
          </div>
        </div>
        <div className="summary-card highlight">
          <span className="summary-label kpi-label">Inactive</span>
          <strong className="summary-value kpi-value empty">{summaryStats.inactive}</strong>
          <div>
            <svg className="kpi-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
          </div>
        </div>
      </div>

      <div className="table-container pad10 userstable">
        <div className="table-header stacked">
          <div className="left-group">
            <div className="search-box">
              <HiOutlineMagnifyingGlass className="search-icon" />
              <input
                type="text"
                placeholder="Search staff..."
                value={searchTerm}
                onChange={handleSearch}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="search-clear-btn"
                  onClick={() => setSearchTerm("")}
                >
                  <HiOutlineXMark size={18} />
                </button>
              )}
            </div>
            <div className="table-info">
              {filteredUsers.length} {filteredUsers.length === 1 ? 'record' : 'records'}
            </div>
          </div>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="empty-state">
            <HiOutlineUser className="empty-icon" />
            <h3>No records found</h3>
            <p>Try adjusting your search terms or add a new person.</p>
          </div>
        ) : (
          <div className="users-table-wrapper">
            <table className="data-table data-table">
              <thead>
                <tr>
                  <th style={{ width: "80px", textAlign: "center" }}>Actions</th>
                  <th>Name / Designation</th>
                  <th>Credentials</th>
                  <th>Role</th>
                  <th>Department</th>
                  <th>Status</th>
                  <th>Contact</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.userId}>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon-edit" onClick={() => handleEdit(user)} title="Edit">
                          <HiOutlinePencil />
                        </button>
                        <button className="btn-icon-delete" onClick={() => setDeleteConfirm(user)} title="Delete">
                          <HiOutlineTrash />
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className="cell-group">
                        <span className="cell-primary">{user.fullName}</span>
                        <span className="cell-secondary">{user.designation || '-'}</span>
                      </div>
                    </td>
                    <td>
                      {user.username ? (
                        <div className="cell-group">
                           <span className="cell-primary" style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                             <HiOutlineLockClosed size={12}/> {user.username}
                           </span>
                        </div>
                      ) : (
                        <span className="cell-secondary">No login</span>
                      )}
                    </td>
                    <td>{getRoleName(user.roleId)}</td>
                    <td>{departments.find(d => d.departmentId === user.departmentId)?.departmentName || '-'}</td>
                    <td>
                      <span className={`status-badge ${user.status?.toLowerCase()}`}>
                        {user.status || 'Active'}
                      </span>
                    </td>
                    <td>
                      <div className="cell-group">
                        <span className="cell-primary">{user.email || '-'}</span>
                        <span className="cell-secondary">{user.phone || '-'}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* User Management Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingUser ? 'Edit Profile' : 'Add New Person'}
        customWidth="800px"
        footer={
          <div className="modal-actions" style={{display: 'flex', justifyContent: 'flex-end', gap: '10px', width: '100%'}}>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="user-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting ? (editingUser ? 'Updating...' : 'Creating...') : (editingUser ? 'Update Profile' : 'Create Person')}
            </button>
          </div>
        }
      >
        <div className="shared-modal-body">
          <div className="drawer-tabs" style={{margin: '-24px -24px 24px -24px'}}>
            <button 
              type="button" 
              className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              Person Profile
            </button>
            <button 
              type="button" 
              className={`tab-btn ${activeTab === 'access' ? 'active' : ''}`}
              onClick={() => setActiveTab('access')}
            >
              System Access
            </button>
          </div>

          <div className="drawer-content" style={{padding: 0, background: 'transparent'}}>
            {error && <div className="error-message drawer-error">{error}</div>}
            <form onSubmit={handleSubmit} id="user-form">
              {activeTab === 'profile' && (
                <div className="tab-pane">
                  <div className="form-section">
                    <h3>Basic Information</h3>
                    <div className="form-row">
                      <div className="form-col">
                        <label>FULL NAME <span className="required">*</span></label>
                        <input
                          type="text"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleInputChange}
                          className={formErrors.fullName ? 'error' : ''}
                          placeholder="Enter full name"
                        />
                        {formErrors.fullName && <span className="field-error">{formErrors.fullName}</span>}
                      </div>
                      <div className="form-col">
                        <label>DESIGNATION</label>
                        <input
                          type="text"
                          name="designation"
                          value={formData.designation}
                          onChange={handleInputChange}
                          placeholder="e.g. Plant Manager"
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-col">
                        <label>EMAIL ADDRESS</label>
                        <input
                          type="text"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={formErrors.email ? 'error' : ''}
                          placeholder="email@example.com"
                        />
                        {formErrors.email && <span className="field-error">{formErrors.email}</span>}
                      </div>
                      <div className="form-col">
                        <label>PHONE NUMBER</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          onKeyDown={handlePhoneKeyDown}
                          className={formErrors.phone ? 'error' : ''}
                          placeholder="+91 00000 00000"
                          maxLength={16}
                        />
                        {formErrors.phone && <span className="field-error">{formErrors.phone}</span>}
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-col">
                        <label>DEPARTMENT</label>
                        <select name="departmentId" value={formData.departmentId} onChange={handleInputChange}>
                          <option value="">Select Department</option>
                          {departments.map(d => (
                            <option key={d.departmentId} value={d.departmentId}>{d.departmentName}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-col">
                        <label>STATUS</label>
                        <select name="status" value={formData.status} onChange={handleInputChange}>
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'access' && (
                <div className="tab-pane">
                  <div className="form-section">
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
                      <h3 style={{margin: 0}}>Login Configuration</h3>
                      <label className="switch-container" style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer'}}>
                        <span style={{fontSize: '13px', fontWeight: 600, color: '#64748b'}}>Enable System Access</span>
                        <div className="switch" style={{marginBottom: 0}}>
                          <input
                            type="checkbox"
                            name="hasLogin"
                            checked={formData.hasLogin}
                            onChange={handleInputChange}
                          />
                          <span className="slider round"></span>
                        </div>
                      </label>
                    </div>

                    {formData.hasLogin ? (
                      <>
                        <div className="form-row">
                          <div className="form-col">
                            <label>USERNAME <span className="required">*</span></label>
                            <input
                              type="text"
                              name="username"
                              value={formData.username}
                              onChange={handleInputChange}
                              className={formErrors.username ? 'error' : ''}
                              placeholder="Enter username"
                              disabled={!!editingUser}
                            />
                            {formErrors.username && <span className="field-error">{formErrors.username}</span>}
                          </div>
                          <div className="form-col">
                            <label>USER ROLE <span className="required">*</span></label>
                            <select 
                              name="roleId" 
                              value={formData.roleId} 
                              onChange={handleInputChange}
                              className={formErrors.roleId ? 'error' : ''}
                            >
                              <option value="">Select Role</option>
                              {roles.map(r => (
                                <option key={r.roleId} value={r.roleId}>{r.roleName}</option>
                              ))}
                            </select>
                            {formErrors.roleId && <span className="field-error">{formErrors.roleId}</span>}
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-col">
                            <label>{editingUser ? 'NEW PASSWORD' : 'PASSWORD'} <span className="required">*</span></label>
                            <input
                              type="password"
                              name="password"
                              value={formData.password}
                              onChange={handleInputChange}
                              placeholder={editingUser ? 'Leave blank to keep' : 'Enter password'}
                            />
                          </div>
                          <div className="form-col">
                            <label>CONFIRM PASSWORD <span className="required">*</span></label>
                            <input
                              type="password"
                              name="confirmPassword"
                              value={formData.confirmPassword}
                              onChange={handleInputChange}
                              placeholder="Confirm password"
                            />
                          </div>
                        </div>
                        <div className="form-section plant-access-section">
                           <div className="plant-access-header">
                             <h3 style={{margin:0}}>Plant Access</h3>
                             {formData.plantIds.length > 0 && (
                               <span className="plant-access-hint">
                                 {formData.plantIds.length === 1 
                                   ? 'Single plant automatically set as default.' 
                                   : 'Multiple plants selected — Default plant functionality removed.'}
                               </span>
                             )}
                           </div>
                           {plants.length === 0 ? (
                             <p style={{fontSize:'13px', color:'#94a3b8', padding:'8px 0'}}>No active plants available.</p>
                           ) : (
                           <div className="plants-checkbox-list">
                              {plants.map(p => {
                                const isChecked = formData.plantIds.includes(p.plantId);
                                const isDefault = formData.defaultPlantId === p.plantId;
                                return (
                                <div
                                  key={p.plantId}
                                  className={`plant-row ${isDefault ? 'is-default' : ''} ${isChecked ? 'is-checked' : ''}`}
                                >
                                  {/* Access checkbox */}
                                  <input
                                    type="checkbox"
                                    id={`p-${p.plantId}`}
                                    className="plant-row-checkbox"
                                    checked={isChecked}
                                    onChange={(e) => {
                                      const id = p.plantId;
                                      setFormData(prev => {
                                        const newIds = e.target.checked
                                          ? [...prev.plantIds, id]
                                          : prev.plantIds.filter(pid => pid !== id);
                                        const newDefault = newIds.length === 1 ? newIds[0] : null;
                                        return { ...prev, plantIds: newIds, defaultPlantId: newDefault };
                                      });
                                    }}
                                  />
                                  <label htmlFor={`p-${p.plantId}`} className="plant-row-label">
                                    <span className="plant-code-badge">{p.plantCode}</span>
                                    {p.plantName}
                                  </label>

                                  {/* Set Default pill - only visible if multiple are selected */}
                                  {formData.plantIds.length > 1 && isChecked && (
                                    <span 
                                      className={`plant-default-pill ${isDefault ? 'active' : ''}`}
                                      onClick={() => setFormData(prev => ({ ...prev, defaultPlantId: p.plantId }))}
                                    >
                                      {isDefault ? 'Default Plant' : 'Set Default'}
                                    </span>
                                  )}

                                  {/* If only one plant is selected, show it as default automatically */}
                                  {formData.plantIds.length === 1 && isChecked && (
                                    <span className="default-pill-preview">Default</span>
                                  )}
                                </div>
                                );
                              })}
                           </div>
                           )}
                        </div>
                      </>
                    ) : (
                      <div className="access-disabled-hint">
                        <HiOutlineShieldCheck size={40} style={{opacity: 0.2, marginBottom: '10px'}}/>
                        <p>This person will be recorded as an employee only. They cannot log in to the system unless System Access is enabled.</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="form-section">
                    <h3>Notification Settings</h3>
                    <div className="notifications-grid">
                      <label className="notification-item">
                        <div className="switch">
                          <input
                            type="checkbox"
                            name="enableEmail"
                            checked={formData.enableEmail}
                            onChange={handleInputChange}
                          />
                          <span className="slider round"></span>
                        </div>
                        <span className="notification-label">Email</span>
                      </label>
                      <label className="notification-item">
                        <div className="switch">
                          <input
                            type="checkbox"
                            name="enableWhatsApp"
                            checked={formData.enableWhatsApp}
                            onChange={handleInputChange}
                          />
                          <span className="slider round"></span>
                        </div>
                        <span className="notification-label">WhatsApp</span>
                      </label>
                      <label className="notification-item">
                        <div className="switch">
                          <input
                            type="checkbox"
                            name="enableSMS"
                            checked={formData.enableSMS}
                            onChange={handleInputChange}
                          />
                          <span className="slider round"></span>
                        </div>
                        <span className="notification-label">SMS</span>
                      </label>
                      <label className="notification-item">
                        <div className="switch">
                          <input
                            type="checkbox"
                            name="enableNotification"
                            checked={formData.enableNotification}
                            onChange={handleInputChange}
                          />
                          <span className="slider round"></span>
                        </div>
                        <span className="notification-label">In-App</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customWidth="500px"
        footer={
          <div className="modal-actions" style={{display: 'flex', justifyContent: 'flex-end', gap: '10px', width: '100%'}}>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirm.userId)}
            >
              Delete User
            </button>
          </div>
        }
      >
        <div className="shared-modal-body">
          <p>Are you sure you want to delete <strong>{deleteConfirm?.fullName}</strong>?</p>
          <p style={{marginTop: '10px', color: 'var(--text-secondary)'}}>
            This action cannot be undone. All access for this user will be revoked.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default Users;
